import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { LoadingState, WaveBackgroundLayout } from "~/common/components";
import { SignUpTeamMemberRender } from "../components/signUp/SignUpTeamMemberRender";
import { TEAM_MEMBER_DATA_TOKEN } from "../constants";
import { useHandleTeamMemberToken } from "../hooks/auth/useHandleTeamMemberToken";
import { useSignUpTeamMember } from "../hooks/auth/useSignUpTeamMember";
import { useGetTeamMemberFromInviteId } from "../hooks/auth/useValidateTeamMemberToken";
import {
  teamMemberSignUpSchema,
  TeamMemberSignUpSchemaType,
} from "../utils/teamMemberSignUpSchema";

export const TeamMemberSignUp = () => {
  const [searchParams] = useSearchParams();
  const dataTokenParam = searchParams.get(TEAM_MEMBER_DATA_TOKEN) ?? undefined;

  const { inviteId, token, extractDataFromToken } =
    useHandleTeamMemberToken(dataTokenParam);

  const {
    formState: { errors, isDirty },
    register,
    watch,
    trigger,
    getValues,
    setValue,
    setError,
    clearErrors,
    control,
  } = useForm<TeamMemberSignUpSchemaType>({
    resolver: zodResolver(teamMemberSignUpSchema),
    mode: "all",
  });

  const { teamMemberFromInviteId, isFetching } =
    useGetTeamMemberFromInviteId(inviteId);

  useEffect(() => {
    if (!teamMemberFromInviteId) {
      extractDataFromToken();
    } else if (!isDirty) {
      setValue("namesAndEmail.name", teamMemberFromInviteId.first_name);
      setValue("namesAndEmail.last_name", teamMemberFromInviteId.last_name);
      setValue("namesAndEmail.email", teamMemberFromInviteId.team_member_email);
    }
  }, [extractDataFromToken, setValue, teamMemberFromInviteId, isDirty]);

  const values = getValues();
  const {
    onBack,
    onNext,
    onRetry,
    canResendAt,
    isActionPending,
    onSubmitSignUp,
  } = useSignUpTeamMember({
    values,
    trigger,
    setError,
    setValue,
    clearErrors,
    inviteId,
    token,
  });

  if (!teamMemberFromInviteId || isFetching)
    return <LoadingState className="h-screen" />;

  return (
    <WaveBackgroundLayout>
      <SignUpTeamMemberRender
        handleRegistration={onSubmitSignUp}
        errors={errors}
        handleGoBack={onBack}
        onNext={onNext}
        onRetry={onRetry}
        canResendAt={canResendAt}
        register={register}
        control={control}
        watch={watch}
        isPending={isActionPending}
      />
    </WaveBackgroundLayout>
  );
};
